import { useState, useCallback, useRef } from 'react';
import { RotateCcw, Trash2, Save } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// 直接导入 Monaco Editor，避免生成 chunk 文件
import Editor from '@monaco-editor/react';

interface CppCodeEditorProps {
  code: string;
  starterCode?: string;
  onSave: () => void;
  onReset: () => void;
  onClear: () => void;
  onChange: (code: string) => void;
  isSaving?: boolean;
  readOnly?: boolean;
  height?: string;
}

export default function CppCodeEditor({
  code,
  starterCode = '',
  onSave,
  onReset,
  onClear,
  onChange,
  isSaving = false,
  readOnly = false,
  height = '400px',
}: CppCodeEditorProps) {
  const editorRef = useRef<any>(null);
  const [confirmAction, setConfirmAction] = useState<'reset' | 'clear' | null>(null);

  const handleReset = useCallback(() => {
    onReset();
    setConfirmAction(null);
  }, [onReset]);

  const handleClear = useCallback(() => {
    onClear();
    setConfirmAction(null);
  }, [onClear]);

  const requestReset = useCallback(() => {
    setConfirmAction('reset');
  }, []);

  const requestClear = useCallback(() => {
    setConfirmAction('clear');
  }, []);

  const cancelConfirm = useCallback(() => {
    setConfirmAction(null);
  }, []);

  const handleSave = useCallback(() => {
    onSave();
  }, [onSave]);

  const handleEditorDidMount = (editor: any) => {
    editorRef.current = editor;
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
      {/* Header - 只在非 readOnly 模式下显示按钮 */}
      {!readOnly && (
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-4 border-b border-blue-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">C++</span>
              </div>
              <div>
                <h3 className="font-bold text-gray-900">C++ 代码编辑器</h3>
                <p className="text-xs text-gray-500">支持 C++17/GCC 编译器</p>
              </div>
            </div>
            <div className="flex items-center gap-2 relative">
              <button
                onClick={requestReset}
                disabled={readOnly}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <RotateCcw className="w-4 h-4" />
                重置
              </button>
              <button
                onClick={requestClear}
                disabled={readOnly}
                className="px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <Trash2 className="w-4 h-4" />
                清空
              </button>
              <button
                onClick={handleSave}
                disabled={isSaving || readOnly}
                className="px-3 py-1.5 text-sm text-blue-600 hover:text-blue-700 hover:bg-white rounded-lg transition-colors flex items-center gap-1.5 disabled:opacity-50"
              >
                <Save className="w-4 h-4" />
                {isSaving ? '保存中...' : '保存代码'}
              </button>
              <AnimatePresence>
                {confirmAction && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={{ opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.15 }}
                    className="absolute right-0 top-full mt-2 z-50 bg-white rounded-xl shadow-lg border border-gray-200 p-4 min-w-[220px]"
                  >
                    <p className="text-sm text-gray-700 mb-3">
                      {confirmAction === 'reset'
                        ? '确定要将代码恢复到初始状态吗？'
                        : '确定要清空所有代码吗？'}
                    </p>
                    <div className="flex items-center gap-2">
                      <button
                        onClick={confirmAction === 'reset' ? handleReset : handleClear}
                        className={`px-3 py-1.5 text-sm rounded-lg transition-colors flex items-center gap-1.5 ${
                          confirmAction === 'clear'
                            ? 'bg-red-50 text-red-600 hover:bg-red-100'
                            : 'bg-blue-50 text-blue-600 hover:bg-blue-100'
                        }`}
                      >
                        {confirmAction === 'reset' ? <RotateCcw className="w-3.5 h-3.5" /> : <Trash2 className="w-3.5 h-3.5" />}
                        确定
                      </button>
                      <button
                        onClick={cancelConfirm}
                        className="px-3 py-1.5 text-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 rounded-lg transition-colors"
                      >
                        取消
                      </button>
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </div>
        </div>
      )}

      {/* Editor - 使用 Monaco Editor */}
      <div className="border-b border-gray-200">
        <Editor
          height={height}
          language="cpp"
          theme="vs-dark"
          value={code}
          onChange={(value) => !readOnly && onChange(value || '')}
          onMount={handleEditorDidMount}
          options={{
            fontSize: 14,
            minimap: { enabled: false },
            scrollBeyondLastLine: false,
            automaticLayout: true,
            tabSize: 2,
            insertSpaces: true,
            readOnly: readOnly,
          }}
        />
      </div>
    </div>
  );
}
